import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaBuilding, FaCheckCircle, FaCreditCard, FaShieldAlt, FaWallet } from 'react-icons/fa';
import '../styles/pages/HowItWorks.css';

const onboardingSteps = [
  {
    number: '01',
    icon: FaBuilding,
    title: 'Register Your Organization',
    description:
      'Businesses that need KYC, verification, and screening services submit an enquiry or onboarding request with their organization details.',
  },
  {
    number: '02',
    icon: FaShieldAlt,
    title: 'Account Review And Approval',
    description:
      'Our team verifies the organization profile and activates access for approved users so the platform is used only for legitimate business operations.',
  },
  {
    number: '03',
    icon: FaCreditCard,
    title: 'Login And Add Wallet Balance',
    description:
      'Approved users log in to the platform and add money to the prepaid wallet through the supported payment flow.',
  },
  {
    number: '04',
    icon: FaWallet,
    title: 'Use Wallet Balance For Services',
    description:
      'Wallet balance is consumed against KYC, PAN, document verification, court case checks, and related service requests placed on the platform.',
  },
];

const paymentFlow = [
  {
    label: 'Access Wallet',
    detail: 'Approved user logs in and opens the wallet area from the dashboard.',
  },
  {
    label: 'Enter Amount',
    detail: 'Recharge amount is selected based on expected service usage.',
  },
  {
    label: 'Secure Payment',
    detail: 'User is redirected to the payment gateway to complete the transaction securely.',
  },
  {
    label: 'Credit Wallet',
    detail: 'Successful payments are confirmed and wallet balance becomes available for service usage.',
  },
  {
    label: 'Run Services',
    detail: 'Wallet credits are consumed against KYC, verification, and compliance requests.',
  },
];

const serviceNotes = [
  'LCS is a B2B service platform and not an ecommerce marketplace.',
  'Payments are used only to recharge a prepaid wallet for digital verification and compliance services.',
  'No physical goods are sold or shipped through this website.',
  'Service delivery is digital and typically starts immediately after successful wallet credit and service request submission.',
];

const trustPoints = ['Approved business access', 'Secure wallet recharge', 'Instant digital service usage'];

export default function HowItWorks() {
  return (
    <div className="how-it-works-page">
      <section className="how-it-works-hero">
        <div className="container">
          <div className="hero-layout">
            <div className="hero-copy">
              <span className="hero-kicker">Platform Guide</span>
              <h1>How LCS Works</h1>
              <p>
                LCS provides wallet-based access to digital KYC and verification services for approved business users.
              </p>

              <div className="hero-trust-row">
                {trustPoints.map((point) => (
                  <span key={point} className="hero-trust-pill">{point}</span>
                ))}
              </div>

              <div className="hero-actions">
                <Link to="/contact" className="hero-button primary">Start With LCS</Link>
                <Link to="/terms-conditions" className="hero-button secondary">View Terms</Link>
              </div>
            </div>

            <div className="hero-visual">
              <div className="hero-orbit hero-orbit-one"></div>
              <div className="hero-orbit hero-orbit-two"></div>
              <div className="hero-glow"></div>

              <div className="hero-summary-card card-float-slow">
                <div className="hero-summary-header">
                  <span className="status-dot"></span>
                  <span>Wallet-Based Service Access</span>
                </div>
                <div className="hero-summary-amount">Approved User → Recharge → Use Services</div>
                <div className="hero-mini-flow">
                  <div className="hero-mini-step active">Review</div>
                  <FaArrowRight />
                  <div className="hero-mini-step active">Payment</div>
                  <FaArrowRight />
                  <div className="hero-mini-step active">Wallet</div>
                </div>
              </div>

              <div className="hero-side-card card-float-fast">
                <div className="hero-side-label">Digital Delivery</div>
                <strong>No physical shipment</strong>
                <p>Services are activated digitally after successful wallet credit and request submission.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works-section insight-strip-section">
        <div className="container">
          <div className="insight-strip">
            <div className="insight-item">
              <span className="insight-value">B2B</span>
              <span className="insight-label">For approved organizations</span>
            </div>
            <div className="insight-item">
              <span className="insight-value">Prepaid</span>
              <span className="insight-label">Wallet funding model</span>
            </div>
            <div className="insight-item">
              <span className="insight-value">Digital</span>
              <span className="insight-label">No physical delivery involved</span>
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works-section how-it-works-overview">
        <div className="container">
          <div className="section-heading">
            <h2>Service Model</h2>
            <p>
              The platform is designed for organizations that need digital verification workflows. Users first get approved,
              then recharge their wallet, and then consume services from the wallet balance.
            </p>
          </div>

          <div className="service-note-grid">
            {serviceNotes.map((note) => (
              <div key={note} className="service-note-card">
                <span className="service-note-mark">•</span>
                <p>{note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="how-it-works-section how-it-works-steps">
        <div className="container">
          <div className="section-heading">
            <h2>Process Overview</h2>
            <p>From onboarding to wallet usage, the operating flow is straightforward and fully digital.</p>
          </div>

          <div className="steps-grid timeline-grid">
            {onboardingSteps.map((step) => (
              <article key={step.number} className="step-card">
                <div className="step-card-top">
                  <div className="step-number">{step.number}</div>
                  <div className="step-icon"><step.icon /></div>
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                <div className="step-line"></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="how-it-works-section payment-flow-section">
        <div className="container">
          <div className="payment-flow-layout">
            <div className="section-heading payment-heading">
              <h2>Wallet Recharge Payment Flow</h2>
              <p>
                Recharge is available to approved users inside the platform, while this page explains the payment journey publicly.
              </p>
            </div>

            <div className="flow-diagram-card">
              <div className="flow-diagram-shell">
                <div className="flow-diagram-badge">Secure Gateway Flow</div>
                <div className="flow-diagram-center">Wallet</div>
                <div className="flow-diagram-node node-user">User</div>
                <div className="flow-diagram-node node-gateway">Payment</div>
                <div className="flow-diagram-node node-service">Services</div>
                <div className="flow-diagram-link link-one"></div>
                <div className="flow-diagram-link link-two"></div>
                <div className="flow-diagram-link link-three"></div>
              </div>
            </div>
          </div>

          <div className="flow-card-grid">
            {paymentFlow.map((item, index) => (
              <div key={item.label} className="flow-card">
                <div className="flow-card-number">{index + 1}</div>
                <div>
                  <h3>{item.label}</h3>
                  <p>{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="how-it-works-section compliance-section">
        <div className="container">
          <div className="compliance-panel">
            <div className="compliance-copy">
              <span className="compliance-tag">Important Clarification</span>
              <h2>This website supports a wallet-funded service workflow</h2>
              <p>
                Payments are collected only for prepaid wallet funding by approved business users. That wallet is then used to access LCS digital verification services.
              </p>
            </div>
            <div className="compliance-points">
              {serviceNotes.map((note) => (
                <div key={note} className="compliance-point">
                  <FaCheckCircle />
                  <span>{note}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works-section support-section">
        <div className="container">
          <div className="support-card">
            <div>
              <h2>Need Help With Billing Or Access?</h2>
              <p>
                For onboarding, wallet recharge questions, payment issues, or service activation support, contact the LCS team.
              </p>
            </div>
            <div className="support-actions">
              <Link to="/contact" className="support-button primary">Contact Support</Link>
              <Link to="/terms-conditions" className="support-button secondary">Terms And Conditions</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}